import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	CircularProgress,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { ResponsiveDialog } from "../ResponsiveDialog";
import Icon from "@mdi/react";
import { mdiPuzzleOutline } from "@mdi/js";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import {
	MemberLibraryServices,
	NotistackServices,
	PublicMemberLibraryDTO,
	PublicTeamMemberDTO,
	SeniorityLevel,
	WorkspaceServices,
} from "shared/services";
import { HandleError } from "shared/utils";

interface HireMemberModalProps {
	open: boolean;
	workspaceId: string;
	teamId: string;
	onClose: () => void;
	onHired: (member: PublicTeamMemberDTO) => void;
}

const SENIORITY_META: Record<SeniorityLevel, { label: string; description: string }> = {
	junior: { label: "Júnior", description: "Modelos rápidos, tarefas simples e conservadoras" },
	pleno: { label: "Pleno", description: "Equilíbrio entre custo, velocidade e qualidade" },
	senior: { label: "Sênior", description: "Modelos avançados, máxima autonomia e criatividade" },
};

const SENIORITY_ORDER: SeniorityLevel[] = ["junior", "pleno", "senior"];

export const HireMemberModal: React.FC<HireMemberModalProps> = ({ open, workspaceId, teamId, onClose, onHired }) => {
	const [memberLibraries, setMemberLibraries] = useState<PublicMemberLibraryDTO[]>([]);
	const [selectedMemberLibraryId, setSelectedMemberLibraryId] = useState<string>("");
	const [memberName, setMemberName] = useState<string>("");
	const [selectedSeniority, setSelectedSeniority] = useState<SeniorityLevel>("pleno");
	const [loading, setLoading] = useState(false);
	const [loadingLibraries, setLoadingLibraries] = useState(true);

	useEffect(() => {
		if (!open) return;

		let cancelled = false;
		setLoadingLibraries(true);

		const loadMemberLibraries = async () => {
			try {
				const data = await MemberLibraryServices.listForWorkspace(workspaceId, true);
				if (cancelled) return;

				setMemberLibraries(data);
				setSelectedMemberLibraryId((current) => current || data[0]?.id || "");
			} catch {
				if (!cancelled) {
					NotistackServices.showErrorMessage("Erro ao carregar templates de membro");
				}
			} finally {
				if (!cancelled) {
					setLoadingLibraries(false);
				}
			}
		};

		void loadMemberLibraries();

		return () => {
			cancelled = true;
		};
	}, [open, workspaceId]);

	const selectedMemberLibrary = useMemo(
		() => memberLibraries.find((memberLibrary) => memberLibrary.id === selectedMemberLibraryId) ?? null,
		[memberLibraries, selectedMemberLibraryId],
	);

	useEffect(() => {
		if (!open) {
			setMemberName("");
			setSelectedSeniority("pleno");
		}
	}, [open]);

	const handleHire = useCallback(async () => {
		if (!selectedMemberLibraryId) {
			NotistackServices.showWarningMessage("Selecione um template de membro");
			return;
		}

		setLoading(true);
		try {
			const member = await WorkspaceServices.hireMember(workspaceId, teamId, {
				member_library_id: selectedMemberLibraryId,
				name: memberName.trim() || undefined,
				seniority_level: selectedSeniority,
			});

			NotistackServices.showSuccessMessage(`${member.name} contratado(a) com sucesso!`);
			onHired(member);
			onClose();
		} catch (error: any) {
			const message = HandleError.isHandleError(error) ? error.message : "Erro ao contratar membro";
			NotistackServices.showErrorMessage(message);
		} finally {
			setLoading(false);
		}
	}, [workspaceId, teamId, selectedMemberLibraryId, memberName, selectedSeniority, onHired, onClose]);

	return (
		<ResponsiveDialog
			open={open}
			onClose={onClose}
			scroll={"paper"}
		>
			<DialogTitle>Contratar Membro</DialogTitle>

			<DialogContent dividers>
				<div className={styles.teamSection}>
					<p className={styles.sectionLabel}>Template de Membro</p>
					{loadingLibraries ? (
						<CircularProgress
							color="inherit"
							size={20}
						/>
					) : memberLibraries.length === 0 ? (
						<p style={{ color: "#b3b3b3", fontSize: "0.875rem" }}>Nenhum template disponível para contratação.</p>
					) : (
						<FormControl
							fullWidth
							size="small"
						>
							<InputLabel id="member-library-select-label">Template</InputLabel>
							<Select
								labelId="member-library-select-label"
								value={selectedMemberLibraryId}
								label="Template"
								onChange={(e) => setSelectedMemberLibraryId(String(e.target.value))}
							>
								{memberLibraries.map((memberLibrary) => (
									<MenuItem
										key={memberLibrary.id}
										value={memberLibrary.id}
									>
										{memberLibrary.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
				</div>

				{selectedMemberLibrary && (
					<div className={styles.profileSection}>
						<div className={styles.avatar}>
							<Icon
								path={mdiPuzzleOutline}
								size={1.5}
								color="#555"
							/>
						</div>
						<div className={styles.profileInfo}>
							<p className={styles.profileName}>{selectedMemberLibrary.name}</p>
							<p className={styles.profileCategory}>{selectedMemberLibrary.description || "Template de membro IA"}</p>
							<div className={styles.profileCompetencies}>
								<span className={styles.competencyTag}>MCPs: {selectedMemberLibrary.mcpIds.length}</span>
								{selectedMemberLibrary.slug && <span className={styles.competencyTag}>Slug: {selectedMemberLibrary.slug}</span>}
							</div>
						</div>
					</div>
				)}

				<div className={styles.teamSection}>
					<p className={styles.sectionLabel}>Nome do Membro (Opcional)</p>
					<TextField
						fullWidth
						size="small"
						value={memberName}
						onChange={(event) => setMemberName(event.target.value)}
						placeholder="Ex: Redator IA"
					/>
				</div>

				<div className={styles.senioritySection}>
					<p className={styles.sectionLabel}>Escolha a Senioridade</p>
					<div className={styles.seniorityOptions}>
						{SENIORITY_ORDER.map((level) => {
							const meta = SENIORITY_META[level];

							return (
								<div
									key={level}
									className={clsx(styles.seniorityOption, selectedSeniority === level && styles.selected)}
									onClick={() => setSelectedSeniority(level)}
								>
									<span className={clsx(styles.seniorityBadge, styles[level])}>{meta.label}</span>
									<div className={styles.seniorityInfo}>
										<div className={styles.seniorityLabel}>{meta.label}</div>
										<div className={styles.seniorityDesc}>{meta.description}</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</DialogContent>

			<DialogActions>
				<Button
					variant="text"
					onClick={onClose}
					disabled={loading}
					sx={{ color: "#b3b3b3", textTransform: "none" }}
				>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleHire}
					disabled={loading || !selectedMemberLibraryId || loadingLibraries || memberLibraries.length === 0}
					sx={{ textTransform: "none", fontWeight: 700 }}
				>
					{loading ? (
						<CircularProgress
							size={20}
							color="inherit"
						/>
					) : (
						"Contratar"
					)}
				</Button>
			</DialogActions>
		</ResponsiveDialog>
	);
};
